/*
 *	Copyright (c) 2011-2021 Acronis Incorporated. All rights reserved.
 *
 *	This source code may be used only by organizations licensed to use Acronis Access, in conjunction with normal Acronis Access usage.
 *	No portion of it may be redistributed or modified without the written permission of Acronis, Inc. See the Acronis Software License Agreement for more information.
 */
angular.module('access.Nodes')
    .factory('UploadFileItem', [
        '$q',
        '$translate',
        'fileSizeFilter',
        'UPLOAD_ITEM_STATUS',
        'UploadItem',
        'UploadTransport',
        function (
            $q,
            $translate,
            fileSizeFilter,
            UPLOAD_ITEM_STATUS,
            UploadItem,
            UploadTransport
        ) {
            /**
             * @class UploadFileItem
             * @classdesc Uploading File Item Constructor
             * @extends UploadItem
             * 
             * @param {File} file - Link to the file instance
             * @param {string} path - File path relative to the choosen folder on the local machine
             */
            function UploadFileItem(file, path) {
                UploadItem.call(this);

                /**
                 * This is not a directory
                 * 
                 * @name UploadFileItem#isDirectory
                 * @type {boolean}
                 * @readonly
                 */
                Object.defineProperty(this, 'isDirectory', {
                    enumerable: true,
                    writable: false,
                    value: false
                });

                /**
                 * File's name
                 *
                 * @name UploadFileItem#name
                 * @type {string} 
                 * @readonly
                 */
                Object.defineProperty(this, 'name', {
                    enumerable: true,
                    get: function () {
                        return file.name;
                    }
                });

                /**
                 * File's path 
                 *
                 * @name UploadFileItem#path
                 * @type {string} 
                 * @readonly
                 */
                Object.defineProperty(this, 'path', {
                    enumerable: true,
                    get: function () {
                        return path;
                    }
                });

                /**
                 * File's size
                 *
                 * @name UploadFileItem#size
                 * @type {number} 
                 * @readonly
                 */
                Object.defineProperty(this, 'size', {
                    enumerable: true,
                    get: function () {
                        return file.size;
                    }
                });

                /**
                 * Human readable file size (in KB, MB, etc.)
                 * 
                 * @name UploadFileItem#sizeFormatted
                 * @type {string}
                 * @readonly
                 */
                Object.defineProperty(this, 'sizeFormatted', {
                    enumerable: true,
                    get: function () {
                        return fileSizeFilter(this.size);
                    }
                });

                /**
                 * File's extension
                 *
                 * @name UploadFileItem#extension
                 * @type {string} 
                 * @readonly
                 */
                Object.defineProperty(this, 'extension', {
                    enumerable: true,
                    get: function () {
                        // 'file.tar.gz' -> 'gz'
                        // 'file' -> undefined
                        // '.dotfile' -> undefined
                        // '.dotfile.rc' -> 'rc'

                        var lastDotIndex = this.name.lastIndexOf('.');

                        return lastDotIndex > 0 ? this.name.substring(lastDotIndex + 1) : undefined;
                    }
                });

                /**
                 * File's root folder. Root means in term of selected folder.
                 * I.e. user uploading local folder /home/bender/folder, which has the file
                 * /home/bender/folder/foo/bar/baz.txt
                 * In this case root folder will be just 'folder'.
                 * 
                 * @name UploadFileItem#rootFolder
                 * @type {string}
                 * @readonly
                 */
                Object.defineProperty(this, 'rootFolder', {
                    enumerable: true,
                    get: function () {
                        return path.split('/')[0] || '';
                    }
                });

                /**
                 * Upload file to the target directory
                 * 
                 * @param {Resource} folder - Target folder resource instance
                 * @param {Function} onProgress - Upload Progress update handler
                 * @returns {Promise}
                 */
                Object.defineProperty(this, 'upload', {
                    enumerable: false,
                    value: function (folder, onProgress) {
                        var that = this;

                        that.status = UPLOAD_ITEM_STATUS.UPLOADING;

                        var uploadProcess = UploadTransport.uploadFile(
                            file,
                            folder.actions.upload,
                            folder.can_be_streamed,
                            function (progress) {
                                that.progress = progress;
                                onProgress();
                            }
                        );

                        _cancelUpload = uploadProcess.cancel;

                        return uploadProcess.promise
                            .then(function () {
                                that.status = UPLOAD_ITEM_STATUS.SUCCESS;
                            })
                            .catch(function (response) {
                                switch (response.status) {
                                    case 409:
                                        that.status = UPLOAD_ITEM_STATUS.CONFLICT;
                                        break;

                                    case 0:
                                    case -1:
                                        that.error = new Error($translate.instant('file_upload_status_failed_message'));
                                        break;

                                    default:
                                        that.error = new Error(
                                            response.data && response.data.error ?
                                                response.data.error.message :
                                                $translate.instant('Error')
                                        );
                                }

                                return $q.reject(response);
                            })
                            .finally(function () {
                                _cancelUpload = null;
                            });
                    }
                });

                var _cancelUpload = null;

                /**
                 * Cancel current upload process
                 */
                Object.defineProperty(this, 'cancelUpload', {
                    value: function () {
                        if (this.isUploading) {
                            this.status = UPLOAD_ITEM_STATUS.CANCELLED;
                            _cancelUpload();
                        }
                    }
                });
            }

            /**
             * Create UploadFile instance from the File
             * 
             * @param {File} file - Instance of the file
             * @returns {UploadFileItem}
             */
            UploadFileItem.fromFile = function (file) {
                var filePath = (file.webkitRelativePath || '').split('/').slice(0, -1).join('/');
                return new UploadFileItem(file, filePath);
            };

            /**
             * Create an Upload File item from Dropped item
             * 
             * @param {DataTransferItem} entry - Dropped Data entry
             * @param {File} file - File instance
             * @returns {UploadFileItem}
             */
            UploadFileItem.fromDrop = function (entry, file) {
                var filePath = entry.fullPath.split('/').slice(1, -1).join('/');
                return new UploadFileItem(file, filePath);
            };

            return UploadFileItem;
        }
    ]);
